import { useCallback, useEffect, useMemo, useRef } from 'react';
import { countUnreadInboxTasks, type InboxItem, type InboxResponse } from '@shared/types/inbox';
import type { TaskLiveMessage } from '@shared/types/tasks';
import { useLoader } from '@/hooks/use-loader';
import { useGuardedNavigate } from '@/hooks/useUnsavedChangesGuard';
import { apiData } from '@/lib/api-contract';
import { describeApiError } from '@/lib/api-errors';
import { INBOX_LOADER_KEY, getInbox, setInboxMarks } from '@/lib/inbox-api';
import { listProjects } from '@/lib/projects-api';
import { openProjectLive } from '@/lib/task-live';

/** The sidebar's count and an Inbox left open load again this often anyway (a run elsewhere moving on). */
const INBOX_POLL_MS = 60_000;
/** Most items in one marks request (worker/src/services/inbox/settings.ts MARKS_MAX). */
const MARKS_AT_ONCE = 200;
/** A burst of changes (a run moving a card and commenting) loads the Inbox once. */
const LIVE_DEBOUNCE_MS = 500;

/** What changes someone's Inbox: a task, its removal, their own marks from another tab, a reconnect. */
const changesInbox = (message: TaskLiveMessage | null) => !message || message.type === 'task' || message.type === 'task_deleted' || message.type === 'inbox';

/**
 * T-37: the signed-in person's Inbox (GET /api/inbox), shared by the sidebar's count and the Inbox
 * page under one loader key. Polled every minute; the page also passes `live`, which opens a socket
 * to each project's hub and loads again when one says something changed. A push the service worker
 * shows loads it again too (`inbox-push`, public/sw.js).
 */
export function useInbox({ live = false }: { live?: boolean } = {}) {
  const loader = useLoader((signal) => getInbox({ signal }), [], { key: INBOX_LOADER_KEY, pollMs: INBOX_POLL_MS });
  const { reload, setData } = loader;
  const response = loader.data?.data;
  const error = loader.data?.error ? describeApiError(loader.data) : null;

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type === 'inbox-push') reload();
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [reload]);

  const projects = useLoader((signal) => listProjects({ signal }), [], { key: 'factory:projects' }).data?.data?.projects;
  const idsKey = live ? (projects ?? []).map((p) => p.id).join(',') : '';
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (!idsKey) return undefined;
    const closers = idsKey.split(',').map((id) =>
      openProjectLive(id, (message) => {
        if (!changesInbox(message)) return;
        clearTimeout(timer.current);
        timer.current = setTimeout(reload, LIVE_DEBOUNCE_MS);
      })
    );
    return () => {
      for (const close of closers) close();
      clearTimeout(timer.current);
    };
  }, [idsKey, reload]);

  /** Lay a change over the items at once, then save it; a failure loads them again. */
  const mark = useCallback(
    async (items: InboxItem[], change: { read?: boolean; done?: boolean }) => {
      if (!items.length) return { error: null as string | null };
      const keys = new Set(items.map((i) => i.key));
      setData((current) => {
        const data = current?.data;
        if (!data) return current ?? apiData<InboxResponse>({ items: [], unread: 0, failed_projects: [] });
        const next = data.items
          .filter((i) => !(change.done && keys.has(i.key)))
          .map((i) => (keys.has(i.key) && change.read !== undefined ? { ...i, read: change.read } : i));
        return apiData({ ...data, items: next, unread: countUnreadInboxTasks(next) });
      });
      // The server takes MARKS_AT_ONCE items a request.
      let error: string | null = null;
      for (let i = 0; i < items.length && !error; i += MARKS_AT_ONCE) {
        const res = await setInboxMarks({ items: items.slice(i, i + MARKS_AT_ONCE).map((x) => ({ project_id: x.project.id, key: x.key })), ...change });
        if (res.error) error = describeApiError(res);
      }
      if (error) reload();
      return { error };
    },
    [setData, reload]
  );

  return useMemo(
    () => ({
      items: response?.items ?? null,
      unread: response?.unread ?? 0,
      failedProjects: response?.failed_projects ?? [],
      /** SF-T-98: notices with no task (an admin's guard catches to look at). */
      notices: response?.notices ?? [],
      error,
      loading: loader.loading,
      refreshing: loader.refreshing,
      reload,
      mark,
    }),
    [response, error, loader.loading, loader.refreshing, reload, mark]
  );
}

/**
 * A click on an Inbox notification while the app is open in a tab: the service worker asks that tab
 * to go to the item (`inbox-open`), so it moves there through the router instead of reloading. It asks
 * first when the page has unsaved changes, as any other move away does. Mounted once, in the app layout.
 */
export function useInboxPushNavigation() {
  const navigate = useGuardedNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null;
      if (data?.type === 'inbox-open' && typeof data.url === 'string' && data.url.startsWith('/inbox')) void navigate(data.url);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);
}
